import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  DEFAULT_POLICY,
  calcEstimate,
  runChecks,
  type CalcContext,
  type CalcPolicy,
  type Check,
  type EstimateCalc,
} from "@/calc";
import { toast } from "sonner";

import type { Ratecard } from "@/calc";
import type { BrainingEstimate } from "./types";
import { createSaveQueue, type SaveQueue } from "./save-queue";
import { contentFingerprint, withApprovalCheck } from "./approval";
import { reviewEstimate, type ReviewResponse } from "./review.functions";
import type { ReviewActionKind } from "./review-flow";
import {
  emptyEstimate,
  library as seedLibrary,
  ratecard as seedRatecard,
  taxScheme,
  type BlocksLibrary,
} from "./mocks";
import { loadLibrary, saveLibrary } from "./library-storage";
import {
  archiveEstimate,
  loadEstimates,
  purgeEstimate,
  saveEstimate,
  trashEstimate,
} from "./estimates-storage";
import { loadRatecard, saveRatecard } from "./ratecard-storage";
import {
  EMPTY_SETTINGS,
  loadCalcSettings,
  saveCalcSettings,
  type CalcSettings,
} from "./policy-storage";

/**
 * Состояние приложения.
 *
 * Хранилище — Postgres, доступ закрыт RLS. В браузере держим копию, чтобы
 * пересчёт при правке часов был мгновенным: считает ядро `src/calc/`, и
 * гонять его через сеть на каждый удар по клавише незачем.
 *
 * Запись отложенная. Правки в таблице расчёта идут очередями по несколько
 * штук в секунду, и сохранять каждую значит держать сервер под градом
 * почти одинаковых запросов.
 */

/** Пауза перед записью. Полсекунды — правки успевают склеиться, потери не заметны. */
const SAVE_DELAY_MS = 500;

interface EstimatesValue {
  estimates: BrainingEstimate[];
  /**
   * Действующая рейткарта. Приходит из базы и правится на своём экране;
   * `mocks/ratecard.json` остаётся только зерном для первого чтения.
   */
  ratecard: Ratecard;
  /** Правка прайса — сохраняем сразу и ждём ответа: это не набор текста. */
  updateRatecard: (next: Ratecard) => Promise<void>;
  /**
   * Как агентство считает: планы, наценки, ставки налогов. Пустые поля —
   * обычное дело: чего не заполнили, берётся из `DEFAULT_POLICY`.
   */
  settings: CalcSettings;
  updateSettings: (next: Omit<CalcSettings, "version">) => Promise<void>;
  /**
   * Справочник блоков работ. Приходит из базы и правится на своём экране;
   * `mocks/blocks-library.json` остаётся зерном для первого чтения.
   *
   * Блок, попавший в смету, копируется туда целиком — поэтому правка
   * справочника не трогает собранные калькуляторы.
   */
  library: BlocksLibrary;
  updateLibrary: (next: BlocksLibrary) => Promise<void>;
  /** Принять справочник, пришедший с сервера (ответ «Обучения»). */
  adoptLibrary: (next: BlocksLibrary) => void;
  /** Политика расчёта: значения по умолчанию, перекрытые справочником. */
  policy: CalcPolicy;
  ready: boolean;
  /** идёт запись — список показывает это, чтобы уход со страницы не пугал */
  saving: boolean;
  error: string | null;
  create: (projectTypeId: string, patch?: Partial<BrainingEstimate>) => Promise<BrainingEstimate>;
  update: (id: string, updater: (e: BrainingEstimate) => BrainingEstimate) => void;
  /**
   * Переход согласования: отправить, отозвать, согласовать, вернуть с
   * замечаниями, переоткрыть. Идёт отдельным запросом, а не правкой сметы —
   * статус на сервере обычная запись не меняет (`review-flow.ts`).
   */
  review: (id: string, action: ReviewActionKind, comment?: string) => Promise<ReviewResponse>;
  trash: (id: string) => void;
  restore: (id: string) => void;
  archive: (id: string, on: boolean) => void;
  purge: (id: string) => void;
  reload: () => void;
}

const Ctx = createContext<EstimatesValue | null>(null);

export function EstimatesProvider({ children }: { children: React.ReactNode }) {
  const [estimates, setEstimates] = useState<BrainingEstimate[]>([]);
  const [ratecard, setRatecard] = useState<Ratecard>(seedRatecard);
  const [settings, setSettings] = useState<CalcSettings>(EMPTY_SETTINGS);
  const [library, setLibrary] = useState<BlocksLibrary>(seedLibrary);

  // Политика — производная от справочника, а не отдельное состояние: планы и
  // наценки живут в базе и правятся на экране, задавать их ещё и откуда-то из
  // кода значило бы держать два источника правды.
  const policy = useMemo<CalcPolicy>(
    () => ({
      ...DEFAULT_POLICY,
      planMargin: settings.planMargin,
      contractorMarkup: settings.markup,
    }),
    [settings],
  );
  const [ready, setReady] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Отложенная запись: по таймеру на смету. Ключ — id, чтобы правки разных
  // калькуляторов не отменяли друг друга.
  const timers = useRef(new Map<string, ReturnType<typeof setTimeout>>());
  const inFlight = useRef(0);
  // Зеркало состояния для таймеров: запись берёт то, что лежит в сторе на
  // момент отправки, а не то, что было при планировании. Раньше за этим
  // ходили через setEstimates с побочным эффектом внутри updater'а — в
  // StrictMode он зовётся дважды, и каждая запись уезжала на сервер два раза.
  const estimatesRef = useRef<BrainingEstimate[]>([]);
  useEffect(() => {
    estimatesRef.current = estimates;
  }, [estimates]);

  const track = useCallback(async (job: () => Promise<unknown>) => {
    inFlight.current += 1;
    setSaving(true);
    try {
      await job();
      setError(null);
    } catch (e) {
      console.error("[estimates] не удалось сохранить", e);
      setError("Не удалось сохранить. Проверьте соединение — правки остались на экране.");
    } finally {
      inFlight.current -= 1;
      if (inFlight.current === 0) setSaving(false);
    }
  }, []);

  const reload = useCallback(() => {
    void (async () => {
      try {
        // Наследство прежнего хранения: в localStorage лежала полная копия
        // смет с именами заказчиков. Теперь данные на сервере, а копия в
        // браузере — просто утечка, которую никто не чистит.
        try {
          window.localStorage.removeItem("braining.estimates.v1");
        } catch {
          // приватный режим — ничего и не сохранялось
        }

        // Рейткарта первой: сметы, собранные до снимка ставок, замораживаются
        // на сервере при чтении, и делать это надо по действующему прайсу.
        const card = await loadRatecard();
        setRatecard(card);

        const [loadedSettings, loadedLibrary, rows] = await Promise.all([
          loadCalcSettings(),
          loadLibrary(),
          loadEstimates(),
        ]);
        setSettings(loadedSettings);
        setLibrary(loadedLibrary);
        setEstimates(rows);
        setError(null);
      } catch (e) {
        console.error("[estimates] не удалось загрузить", e);
        setError("Не удалось загрузить калькуляторы.");
      } finally {
        setReady(true);
      }
    })();
  }, []);

  useEffect(reload, [reload]);

  /**
   * Запись одной сметы — с проверкой, что её не перезаписали из другой вкладки.
   *
   * Конфликт — не ошибка сети: показываем то, что лежит в базе, и говорим,
   * что правка не записана. Тихо перетереть чужую версию было бы хуже.
   */
  // Отметки записи — отдельно от состояния: ответ сервера приходит раньше,
  // чем React доведёт `savedAt` до `estimatesRef`, а следующая запись из
  // очереди стартует сразу. Берём отметку отсюда — она всегда последняя.
  const savedAt = useRef(new Map<string, string>());

  const persist = useCallback(async (id: string) => {
    const fresh = estimatesRef.current.find((e) => e.id === id);
    if (!fresh) return;
    const estimate: BrainingEstimate = {
      ...fresh,
      savedAt: savedAt.current.get(id) ?? fresh.savedAt,
    };
    const saved = await saveEstimate({ data: { id, data: estimate } });
    if ("conflict" in saved) {
      savedAt.current.set(id, saved.current.savedAt ?? "");
      setEstimates((prev) => prev.map((e) => (e.id === id ? saved.current : e)));
      toast.error("Смету изменили в другой вкладке — показана последняя версия", {
        id: `conflict-${id}`,
        description: "Ваша последняя правка не записана. Внесите её заново.",
      });
      return;
    }
    // Отметка записи — в локальную копию, иначе следующая запись сочтётся
    // конфликтом с самой собой. Правки, сделанные за время запроса, остаются.
    if (saved.savedAt) savedAt.current.set(id, saved.savedAt);
    setEstimates((prev) => prev.map((e) => (e.id === id ? { ...e, savedAt: saved.savedAt } : e)));
  }, []);

  // Записи одной сметы идут по очереди: следующая ждёт ответа предыдущей и
  // уходит уже с новой отметкой. Иначе вторая правка подряд конфликтовала
  // сама с собой и стиралась с экрана как «изменённая в другой вкладке».
  const queue = useRef<SaveQueue | null>(null);
  if (!queue.current) {
    queue.current = createSaveQueue((id) => track(() => persist(id)));
  }

  /** Записать всё, что ждёт таймера, — сейчас. */
  const flush = useCallback(() => {
    for (const [id, t] of timers.current) {
      clearTimeout(t);
      timers.current.delete(id);
      void queue.current!.push(id);
    }
  }, []);

  // Незаписанное при уходе со страницы дописываем сразу — и при размонтировании
  // стора, и при закрытии вкладки. Раньше здесь стояла отмена таймеров: до
  // полсекунды правок пропадало молча, а комментарий обещал обратное.
  useEffect(() => {
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      if (timers.current.size === 0 && inFlight.current === 0 && !queue.current?.busy()) return;
      flush();
      // Браузер не обязан дождаться запроса — просим человека подтвердить уход.
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", onBeforeUnload);
      flush();
    };
  }, [flush]);

  const scheduleSave = useCallback((estimate: BrainingEstimate) => {
    const existing = timers.current.get(estimate.id);
    if (existing) clearTimeout(existing);

    timers.current.set(
      estimate.id,
      setTimeout(() => {
        timers.current.delete(estimate.id);
        void queue.current!.push(estimate.id);
      }, SAVE_DELAY_MS),
    );
  }, []);

  const updateRatecard = useCallback(
    async (next: Ratecard) => {
      // Показываем сразу, чтобы правка не «отскакивала», но ошибку не
      // проглатываем: откатываем на то, что реально лежит в базе.
      const before = ratecard;
      setRatecard(next);
      try {
        const saved = await saveRatecard({ data: { data: next } });
        setRatecard(saved);
        setError(null);
      } catch (e) {
        console.error("[ratecard] не удалось сохранить", e);
        setRatecard(before);
        setError("Не удалось сохранить рейткарту.");
        throw e;
      }
    },
    [ratecard],
  );

  const updateSettings = useCallback(
    async (next: Omit<CalcSettings, "version">) => {
      // Показываем сразу, чтобы правка не «отскакивала», но ошибку не
      // проглатываем: откатываем на то, что реально лежит в базе.
      const before = settings;
      setSettings({ ...settings, ...next });
      try {
        const saved = await saveCalcSettings({ data: next });
        setSettings(saved);
        setError(null);
      } catch (e) {
        console.error("[policy] не удалось сохранить", e);
        setSettings(before);
        setError("Не удалось сохранить справочник.");
        throw e;
      }
    },
    [settings],
  );

  const updateLibrary = useCallback(
    async (next: BlocksLibrary) => {
      // Показываем сразу, чтобы правка не «отскакивала», но ошибку не
      // проглатываем: откатываем на то, что реально лежит в базе.
      const before = library;
      setLibrary(next);
      try {
        const saved = await saveLibrary({ data: { data: next } });
        if ("conflict" in saved) {
          // Справочник успели записать в другом месте — на экране «Обучение»,
          // в другой вкладке или автоприменением. Показываем то, что в базе:
          // тихо перетереть чужую правку хуже, чем попросить повторить свою.
          setLibrary(saved.current);
          toast.error("Справочник изменили в другом месте — показан свежий", {
            id: "library-conflict",
            description: "Ваша правка не записана. Повторите её на обновлённом справочнике.",
          });
          return;
        }
        setLibrary(saved);
        setError(null);
      } catch (e) {
        console.error("[library] не удалось сохранить", e);
        setLibrary(before);
        setError("Не удалось сохранить справочник блоков.");
        throw e;
      }
    },
    [library],
  );

  /**
   * Справочник, пришедший не от нас: ответ «Обучения» после автоприменения.
   *
   * Стор читает справочник один раз за сессию, а автоприменение правит его на
   * сервере при каждом заходе в панель. Без этого следующая правка с экрана
   * ушла бы со старой отметкой и получила конфликт на ровном месте.
   */
  const adoptLibrary = useCallback((next: BlocksLibrary) => setLibrary(next), []);

  const create = useCallback(
    async (projectTypeId: string, patch?: Partial<BrainingEstimate>) => {
      const draft = { ...emptyEstimate(projectTypeId, library), ...patch };
      // Идентификатор выдаёт база: генерировать его на клиенте значит
      // рисковать столкновением и расхождением с ключом строки.
      const saved = await saveEstimate({ data: { data: draft } });
      if ("conflict" in saved) throw new Error("новая смета не может конфликтовать");
      setEstimates((prev) => [saved, ...prev]);
      return saved;
    },
    [library],
  );

  const update = useCallback(
    (id: string, updater: (e: BrainingEstimate) => BrainingEstimate) => {
      setEstimates((prev) => {
        let dropped = false;
        const next = prev.map((e) => {
          if (e.id !== id) return e;
          const checked = withApprovalCheck(e, updater(e));
          dropped ||= checked.dropped;
          return checked.estimate;
        });
        const changed = next.find((e) => e.id === id);
        if (changed) scheduleSave(changed);
        // Идентификатор у тоста — чтобы в StrictMode, где updater зовётся
        // дважды, сообщение не раздваивалось.
        if (dropped) {
          toast.message("Смета изменена после согласования — статус снят", {
            id: "approval-dropped",
            description:
              "Руководитель согласовывал другую версию. Калькулятор снова выходит с пометкой «черновик» — отправьте его на согласование заново.",
          });
        }
        return next;
      });
    },
    [scheduleSave],
  );

  /**
   * Переход согласования.
   *
   * Порядок важен. Сначала дописываем всё, что ждёт таймера или летит:
   * сервер применит переход к тому, что лежит в базе, и версия без последней
   * правки ушла бы руководителю на согласование. Потом снимаем отпечаток с
   * локальной копии — после дозаписи она по содержанию равна серверной, и
   * расхождение отпечатков значит, что смету изменил кто-то ещё.
   *
   * Ответ накладывается поверх локальной сметы только полями согласования:
   * правки, сделанные за время запроса, остаются на месте и уйдут следующей
   * записью уже с новой отметкой.
   */
  const review = useCallback(
    async (id: string, action: ReviewActionKind, comment?: string): Promise<ReviewResponse> => {
      const timer = timers.current.get(id);
      if (timer) {
        clearTimeout(timer);
        timers.current.delete(id);
        void queue.current!.push(id);
      }
      await queue.current!.idle(id);

      const local = estimatesRef.current.find((e) => e.id === id);
      if (!local) return { ok: false, code: "not_found", error: "Калькулятор не найден." };

      const res = await reviewEstimate({
        data: { id, action, comment, fingerprint: contentFingerprint(local) },
      });
      if (!res.ok) {
        // Статус или содержание в базе не те, что на экране: смету трогал
        // кто-то ещё. Показываем то, что есть на самом деле.
        if (res.code === "stale" || res.code === "wrong_status") reload();
        return res;
      }

      if (res.patch.savedAt) savedAt.current.set(id, res.patch.savedAt);
      setEstimates((prev) =>
        prev.map((e) => {
          if (e.id !== id) return e;
          const next = { ...e } as Record<string, unknown>;
          // Снятое поле приходит как null — убираем ключ, а не храним null:
          // `estimateStatus` смотрит на истинность, но тип поля — строка.
          for (const [key, value] of Object.entries(res.patch)) {
            if (value === null || value === undefined) delete next[key];
            else next[key] = value;
          }
          return next as unknown as BrainingEstimate;
        }),
      );
      return res;
    },
    [reload],
  );

  /** Отметки архива и корзины пишутся сразу: это осознанные действия, не набор текста. */
  const stamp = useCallback(
    (id: string, field: "archivedAt" | "trashedAt", on: boolean) => {
      setEstimates((prev) =>
        prev.map((e) =>
          e.id === id ? { ...e, [field]: on ? new Date().toISOString() : undefined } : e,
        ),
      );
      const call = field === "archivedAt" ? archiveEstimate : trashEstimate;
      void track(() => call({ data: { id, on } }));
    },
    [track],
  );

  const trash = useCallback((id: string) => stamp(id, "trashedAt", true), [stamp]);
  const restore = useCallback((id: string) => stamp(id, "trashedAt", false), [stamp]);
  const archive = useCallback((id: string, on: boolean) => stamp(id, "archivedAt", on), [stamp]);

  const purge = useCallback(
    (id: string) => {
      setEstimates((prev) => prev.filter((e) => e.id !== id));
      void track(() => purgeEstimate({ data: { id } }));
    },
    [track],
  );

  const value = useMemo<EstimatesValue>(
    () => ({
      estimates,
      ratecard,
      updateRatecard,
      settings,
      updateSettings,
      library,
      updateLibrary,
      adoptLibrary,
      policy,
      ready,
      saving,
      error,
      create,
      update,
      review,
      trash,
      restore,
      archive,
      purge,
      reload,
    }),
    [
      estimates,
      ratecard,
      updateRatecard,
      settings,
      updateSettings,
      library,
      updateLibrary,
      adoptLibrary,
      policy,
      ready,
      saving,
      error,
      create,
      update,
      review,
      trash,
      restore,
      archive,
      purge,
      reload,
    ],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useEstimates(): EstimatesValue {
  const v = useContext(Ctx);
  if (!v) throw new Error("useEstimates вызван вне EstimatesProvider");
  return v;
}

export interface EstimateView {
  estimate: BrainingEstimate;
  calc: EstimateCalc;
  checks: Check[];
  ctx: CalcContext;
  patch: (updater: (e: BrainingEstimate) => BrainingEstimate) => void;
}

/** Смета вместе с пересчётом и проверками. Пересчёт — при каждом изменении. */
export function useEstimate(id: string): EstimateView | null {
  const { estimates, ratecard, policy, library, update } = useEstimates();
  const estimate = estimates.find((e) => e.id === id);

  const patch = useCallback(
    (updater: (e: BrainingEstimate) => BrainingEstimate) => update(id, updater),
    [id, update],
  );

  return useMemo(() => {
    if (!estimate) return null;
    const ctx: CalcContext = {
      ratecard,
      // Схемы живут в том же справочнике: юрлицо и его НДС агентство правит
      // там же, где блоки.
      scheme: taxScheme(estimate.taxSchemeId, library),
      policy,
    };
    const calc = calcEstimate(estimate, ctx);
    return { estimate, calc, checks: runChecks(estimate, calc, ctx), ctx, patch };
  }, [estimate, ratecard, policy, library, patch]);
}
